import {
  Button,
  Card,
  Dialog,
  DialogPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
} from "@tremor/react";
import { ShieldCheck, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getMemberPermissions,
  setPermissionOverride,
  deletePermissionOverride,
} from "../api/permissions.js";
// Member/invitation mutations call the API directly; the page invalidates the
// relevant caches after success.
import {
  createWorkspaceInvitation,
  removeMember,
  revokeWorkspaceInvitation,
  updateMemberRole,
} from "../api/workspaces.js";
import { useMembers, useWorkspaceInvitations } from "../queries/workspaces.js";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import { usePermission } from "../lib/usePermission.js";
import { useTranslation } from "react-i18next";

const MEMBER_ROLES = ["owner", "admin", "member", "viewer"];

// Permissions that can be overridden (mirrors lib/rbac.py OVERRIDABLE)
const PERMISSION_GROUPS = [
  {
    key: "monitors",
    perms: [
      "monitors.view",
      "monitors.create",
      "monitors.edit",
      "monitors.delete",
      "monitors.trigger",
    ],
  },
  { key: "incidents", perms: ["incidents.view", "incidents.manage"] },
  { key: "alerts", perms: ["alerts.view", "alerts.manage"] },
  { key: "channels", perms: ["channels.view", "channels.manage"] },
  { key: "status_pages", perms: ["status_pages.view", "status_pages.manage"] },
  { key: "maintenance", perms: ["maintenance.view", "maintenance.manage"] },
  { key: "channels", perms: ["channels.view", "channels.manage"] },
  { key: "groups", perms: ["groups.view", "groups.manage"] },
  {
    key: "team",
    perms: ["team.view", "team.invite", "team.manage"],
  },
  { key: "settings", perms: ["settings.view", "settings.edit"] },
  { key: "api_keys", perms: ["api_keys.manage"] },
];

function PermissionsPanel({ member, onClose }) {
  const { t } = useTranslation("members");
  const [overrideMap, setOverrideMap] = useState({});
  const [resolvedSet, setResolvedSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // permission string being saved

  useEffect(() => {
    setLoading(true);
    getMemberPermissions(member.user_id)
      .then((data) => {
        setResolvedSet(new Set(data.resolved ?? []));
        setOverrideMap(data.overrides ?? {});
      })
      .catch(() => toast.error(t("permissions.success_load_error")))
      .finally(() => setLoading(false));
  }, [member.user_id, t]);

  async function setEffect(perm, effect) {
    setSaving(perm);
    try {
      if (effect === "default") {
        await deletePermissionOverride(member.user_id, perm);
        setOverrideMap((prev) => {
          const next = { ...prev };
          delete next[perm];
          return next;
        });
      } else {
        await setPermissionOverride(member.user_id, perm, effect);
        setOverrideMap((prev) => ({ ...prev, [perm]: effect }));
      }
    } catch (err) {
      toast.error(err.response?.data?.error ?? t("permissions.success_update_error"));
    } finally {
      setSaving(null);
    }
  }

  const canOverride = member.role === "member" || member.role === "viewer";

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col overflow-hidden rounded-none bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              {t("permissions.title", { email: member.email })}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 capitalize">
              {t("permissions.role", { role: t(`roles.${member.role}`) })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-400">{t("loading")}</p>
          ) : !canOverride ? (
            <p className="text-sm text-gray-500">
              {t("permissions.overrides_info")}
            </p>
          ) : (
            <div className="space-y-6">
              <p className="text-xs text-gray-500">
                {t("permissions.immediate_info")}
              </p>

              {PERMISSION_GROUPS.map(({ key, perms }) => (
                <div key={key}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {t(`groups.${key}`)}
                  </h3>
                  <div className="divide-y divide-gray-50 rounded-lg border border-gray-100">
                    {perms.map((perm) => {
                      const currentEffect = overrideMap[perm] ?? "default";
                      const isResolved = resolvedSet.has(perm);
                      const isSaving = saving === perm;
                      const action = perm.split(".")[1];

                      return (
                        <div
                          key={perm}
                          className="flex items-center justify-between gap-4 px-3 py-2.5"
                        >
                          <div>
                            <span className="text-sm text-gray-700">
                              {t(`perm_actions.${action}`)}
                            </span>
                            {currentEffect === "default" && (
                              <span
                                className={`ml-2 text-xs ${isResolved ? "text-teal-600" : "text-gray-400"}`}
                              >
                                ({isResolved ? t("permissions.on_by_default") : t("permissions.off_by_default")})
                              </span>
                            )}
                          </div>
                          <div className="flex rounded-md border border-gray-200 text-xs">
                            {["default", "allow", "deny"].map((eff) => (
                              <button
                                key={eff}
                                disabled={isSaving}
                                onClick={() => setEffect(perm, eff)}
                                className={`px-2.5 py-1 capitalize first:rounded-l-md last:rounded-r-md disabled:opacity-50 ${
                                  currentEffect === eff
                                    ? eff === "allow"
                                      ? "bg-teal-500 text-white"
                                      : eff === "deny"
                                        ? "bg-red-500 text-white"
                                        : "bg-gray-600 text-white"
                                    : "text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {t(`permissions.${eff}`)}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("common:common.close")}
          </Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

function Section({ title, children, description }) {
  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </Card>
  );
}

export default function Members() {
  const { t } = useTranslation(["members", "common"]);
  const can = usePermission();
  const [invitationEmail, setInvitationEmail] = useState("");
  const [invitationRole, setInvitationRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [memberActionUserId, setMemberActionUserId] = useState(null);
  const [invitationActionId, setInvitationActionId] = useState(null);
  const [permsPanelMember, setPermsPanelMember] = useState(null);

  const qc = useQueryClient();
  const membersQuery = useMembers();
  // Invitations: viewer/member roles get a 403; the hook's retry rule already
  // short-circuits, and we let the empty array fall through silently.
  const invitationsQuery = useWorkspaceInvitations();
  const members = membersQuery.data ?? [];
  const invitations = invitationsQuery.data ?? [];
  const loading = membersQuery.isPending;

  useEffect(() => {
    if (membersQuery.isError) toast.error("Failed to load members");
  }, [membersQuery.isError]);

  const refreshMembers = () =>
    qc.invalidateQueries({ queryKey: keys.members.all });
  const refreshInvitations = () =>
    qc.invalidateQueries({ queryKey: ["workspace-invitations"] });

  async function handleInviteMember(e) {
    e.preventDefault();
    if (!invitationEmail.trim()) return;

    setInviting(true);
    try {
      await createWorkspaceInvitation({
        email: invitationEmail.trim(),
        role: invitationRole,
      });
      refreshInvitations();
      setInvitationEmail("");
      setInvitationRole("member");
      toast.success(t("invite.success"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to create workspace invitation");
    } finally {
      setInviting(false);
    }
  }

  async function handleUpdateMemberRole(userId, role) {
    setMemberActionUserId(userId);
    try {
      await updateMemberRole(userId, role);
      refreshMembers();
      toast.success(t("current.success_update"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to update workspace member");
    } finally {
      setMemberActionUserId(null);
    }
  }

  async function handleRemoveMember(userId) {
    setMemberActionUserId(userId);
    try {
      await removeMember(userId);
      refreshMembers();
      toast.success(t("current.success_remove"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to remove workspace member");
    } finally {
      setMemberActionUserId(null);
    }
  }

  async function handleRevokeInvitation(invitationId) {
    setInvitationActionId(invitationId);
    try {
      await revokeWorkspaceInvitation(invitationId);
      refreshInvitations();
      toast.success(t("pending.success_revoke"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to revoke workspace invitation");
    } finally {
      setInvitationActionId(null);
    }
  }

  if (loading) {
    return (
      <div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="text-sm text-gray-400">{t("loading_members")}</p>
      </div>
    );
  }

  const canManageTeam = can("team.manage");
  const canManagePermissions = can("permissions.manage");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
      </div>

      {canManageTeam && (
        <Section
          title={t("invite.title")}
          description={t("invite.description")}
        >
          <form
            onSubmit={handleInviteMember}
            className="grid grid-cols-[1.5fr_180px_auto] gap-3"
          >
            <TextInput
              type="email"
              value={invitationEmail}
              onValueChange={setInvitationEmail}
              placeholder={t("invite.email_placeholder")}
            />
            <select
              value={invitationRole}
              onChange={(e) => setInvitationRole(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
            >
              {MEMBER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`roles.${role}`)}
                </option>
              ))}
            </select>
            <Button type="submit" disabled={inviting || !invitationEmail.trim()}>
              {inviting ? t("invite.inviting") : t("invite.button")}
            </Button>
          </form>
        </Section>
      )}

      {canManageTeam && (
        <Section
          title={t("pending.title")}
          description={t("pending.description")}
        >
          {invitations.length === 0 ? (
            <p className="text-sm text-gray-400">{t("pending.empty")}</p>
          ) : (
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {invitation.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("pending.role_invited_by", { role: t(`roles.${invitation.role}`), email: invitation.invited_by_email })}
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="xs"
                    disabled={invitationActionId === invitation.id}
                    onClick={() => handleRevokeInvitation(invitation.id)}
                  >
                    {t("pending.revoke")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <Section
        title={t("current.title")}
        description={t("current.description")}
      >
        {members.length === 0 ? (
          <p className="text-sm text-gray-400">{t("current.empty")}</p>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t("current.email")}</TableHeaderCell>
                <TableHeaderCell>{t("current.name")}</TableHeaderCell>
                <TableHeaderCell>{t("current.role")}</TableHeaderCell>
                <TableHeaderCell>{t("current.actions")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.user_id}>
                  <TableCell className="text-gray-700">{member.email}</TableCell>
                  <TableCell className="text-gray-500">
                    {member.full_name ?? "—"}
                  </TableCell>
                  <TableCell>
                    {canManageTeam ? (
                      <select
                        value={member.role}
                        disabled={memberActionUserId === member.user_id}
                        onChange={(e) =>
                          handleUpdateMemberRole(member.user_id, e.target.value)
                        }
                        className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 disabled:opacity-40"
                      >
                        {MEMBER_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {t(`roles.${role}`)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="capitalize text-sm text-gray-600">
                        {t(`roles.${member.role}`)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {canManagePermissions &&
                        (member.role === "member" || member.role === "viewer") && (
                          <Button
                            variant="secondary"
                            size="xs"
                            onClick={() => setPermsPanelMember(member)}
                            icon={ShieldCheck}
                          >
                            {t("current.permissions")}
                          </Button>
                        )}
                      {canManageTeam && (
                        <Button
                          variant="secondary"
                          size="xs"
                          color="red"
                          disabled={memberActionUserId === member.user_id}
                          onClick={() => handleRemoveMember(member.user_id)}
                          icon={Trash2}
                        >
                          {t("current.remove")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      {permsPanelMember && (
        <PermissionsPanel
          member={permsPanelMember}
          onClose={() => setPermsPanelMember(null)}
        />
      )}
    </div>
  );
}
